import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { api, ApiRequestError } from '@/lib/api'
import { tickSocket } from '@/feed/tickSocket'
import {
  orderStore,
  type OrderBroker,
  type TrackedIntent,
} from '@/store/orders'
import { notifyFail } from '@/components/Toast'
import { pacingDetail, type ExitNotice } from './exitNotice'
import { provisionalStore } from './provisionalStore'
import { isOrderWindow, type ExitFraction } from '@apna/shared'
import type { OpenBookState } from './useOpenBook'

/**
 * `tradeIds` rather than `tradeId` so "Exit all" on the manual list is ONE
 * request: every chain click is a Trade of its own, so that block is N
 * trades, and sending their TOKENS instead would scope the server's query
 * to the whole account and close a strategy leg sharing one of those
 * strikes. One call also means one plan, so BUYs are still ordered first
 * across the whole set.
 */
interface ExitBody {
  connectionId: string
  tradeIds: string[]
  tokens?: number[]
  /**
   * ONE named leg — 5.15. Several legs can share a token, so `tokens` means
   * "every leg on this contract" and cannot express a single row's ticket.
   */
  tradeLegId?: string
  /** 25/50/75/100. A FRACTION, never a quantity — see `partialExit.ts`. */
  fraction?: ExitFraction
  /** Rest the closing order here. Absent sends MARKET, as Exit always has. */
  limitPrice?: number
}
type ExitResponse =
  | {
      ok: true
      intentId: string
      clientRequestId: string
      legCount: number
      skipped: { token: number; symbol: string | null; why: string }[]
    }
  | { ok: false; reason: string }
/**
 * What became of ONE account's exit — three-valued, never a boolean.
 *
 * `UNCERTAIN` is the whole reason this is not `ok: boolean`. A send that never
 * answered may have reached the broker, so a caller fanning this out across
 * several accounts must not count it as "not exited" — §5.6, the same rule
 * `positionsFor` follows about a position book that cannot be read.
 *
 * `IGNORED` is a call with nothing to report: a duplicate press on the same
 * control, or one whose entry was replaced by a later press. Both were silent
 * before this returned anything, and stay silent.
 */
export type ExitOutcome =
  | { readonly kind: 'ACCEPTED' }
  | { readonly kind: 'REFUSED'; readonly why: string }
  | { readonly kind: 'UNCERTAIN'; readonly why: string }
  | { readonly kind: 'IGNORED' }

/**
 * Is this exit over, and how — ROADMAP 5.12, 5.15.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * PURE, BECAUSE THIS IS THE PART THAT WAS WRONG
 *
 * It lived inside the effect as a condition with three branches, and the one
 * added for partial exits was unreachable: a 50% close leaves the leg
 * holding something, so "nothing we named is still held" could never become
 * true and the banner span forever over an order the book had already
 * filled. Reported 2026-11-01.
 *
 * A condition that decides when a spinner stops is worth being able to test
 * without a browser.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * TWO DIFFERENT CONFIRMATIONS, AND THE WEAKER ONE IS DELIBERATE
 *
 *   FULL exit     confirmed against the POSITION BOOK — nothing we named is
 *                 held any more. The strongest statement available, and the
 *                 right one: "the order filled" is a claim about an order,
 *                 while what the trader asked for was to be flat.
 *
 *   PARTIAL exit  confirmed against the ORDER — every leg of it FILLED.
 *                 Weaker, and the strongest one that EXISTS here: half a leg
 *                 is still a leg, so the position book cannot answer, and
 *                 "the quantity went down" cannot be tested against a
 *                 `netQty` that moves for every other reason too.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export type ExitSettlement =
  /** Still in flight. Keep the spinner. */
  | { readonly kind: 'WAITING' }
  | { readonly kind: 'DONE'; readonly notice: ExitNotice }
  /** Over, and something did not close. The sentence is for the banner. */
  | { readonly kind: 'FAILED'; readonly why: string }

export function exitSettlement(input: {
  readonly partial: boolean
  readonly accepted: boolean
  readonly tradeIds: readonly string[]
  readonly legIds: readonly string[]
  /** The order-book row for this request, when one has arrived. */
  readonly order:
    | Pick<TrackedIntent, 'state' | 'intentId' | 'message' | 'legs'>
    | undefined
  /** READY and its trades, or null when the book cannot be read. */
  readonly trades:
    | readonly {
        tradeId: string
        legs: readonly { tradeLegId: string; netQty: number }[]
      }[]
    | null
}): ExitSettlement {
  const { partial, accepted, tradeIds, legIds, order, trades } = input

  /*
   * FAILURE FIRST, and it is judged on the ORDER whether the exit was
   * partial or not: an order that is over without filling is over, and the
   * position book has nothing to add about why.
   */
  const terminalFailure =
    order !== undefined &&
    (order.legs.length === 0
      ? ['REFUSED', 'REJECTED', 'CANCELLED'].includes(order.state)
      : order.legs.every((l) =>
          ['FILLED', 'REFUSED', 'REJECTED', 'CANCELLED'].includes(l.state),
        ) && order.legs.some((l) => l.state !== 'FILLED'))
  if (terminalFailure && order !== undefined) {
    return {
      kind: 'FAILED',
      why:
        order.message ??
        order.legs.find((l) => l.reason)?.reason ??
        'The exit did not close every requested leg. Check the remaining positions.',
    }
  }

  // Nothing is concluded until the server has acknowledged it.
  if (!accepted && !order?.intentId) return { kind: 'WAITING' }

  /*
   * ── A RESTING LIMIT IS A CONCLUSION, NOT A WAIT — ROADMAP 9.33 ──────────
   *
   * An exit at MARKET settles in about 120 ms and "waiting" is the honest
   * thing to say for that span. A LIMIT does not settle at all until the
   * market reaches it, which may be never — a SELL at 500 against an LTP of
   * 157 is a standing instruction, not a request in flight.
   *
   * Judged as WAITING, it was: the banner said "awaiting execution — do not
   * submit it again" indefinitely, the leg sat on EXITING with its cross
   * replaced by a spinner, and the basket's EXIT stayed disabled. The
   * trader had placed a perfectly ordinary order and lost the use of the
   * position.
   *
   * So an order whose every leg is ACKED and unfilled is DONE for this
   * hook's purposes. Nothing is lost by releasing it: the order is in
   * PENDING ORDERS with Cancel and Fill-at-market beside it, which is where
   * a working order belongs, and `alreadySent` on the server still reserves
   * its quantity so a second exit cannot double-send.
   */
  const resting =
    order !== undefined &&
    order.legs.length > 0 &&
    order.legs.every((l) => l.state === 'ACKED' && l.filledQty === 0)
  if (resting) {
    return {
      kind: 'DONE',
      notice: {
        headline: 'Exit order placed · resting until the market reaches it.',
        detail:
          'It is under Pending orders, with Cancel and Fill at market beside it.',
      },
    }
  }

  if (partial) {
    const filled =
      order !== undefined &&
      order.legs.length > 0 &&
      order.legs.every((l) => l.state === 'FILLED')
    return filled
      ? {
          kind: 'DONE',
          notice: { headline: 'Exit complete · the order filled.' },
        }
      : { kind: 'WAITING' }
  }

  // A book we could not read says nothing; it never says "flat".
  if (trades === null) return { kind: 'WAITING' }
  /*
   * Across SEVERAL trades this has to look at all of them — stopping at the
   * first would call a multi-trade exit done while the rest were still open.
   */
  const stillHeld = trades.some(
    (t) =>
      tradeIds.includes(t.tradeId) &&
      t.legs.some((l) => legIds.includes(l.tradeLegId) && l.netQty !== 0),
  )
  return stillHeld
    ? { kind: 'WAITING' }
    : {
        kind: 'DONE',
        notice: { headline: 'Exit complete · position book confirmed.' },
      }
}

interface PendingExit {
  which: string
  tradeIds: string[]
  legIds: string[]
  requestId: string
  accepted: boolean
  startedAt: number
  /**
   * Was only PART of the leg asked for — ROADMAP 5.15.
   *
   * It changes what "done" means, and getting that wrong is the bug this
   * field exists for: a 50% exit leaves the leg holding something, so the
   * position-book test below ("nothing we named is still held") can never
   * become true and the banner span forever over an order the book already
   * showed as Executed. Reported 2026-11-01.
   */
  partial: boolean
}

/**
 * A REFUSED EXIT IS TOASTED, NOT ONLY BANNERED — ROADMAP 5.12.
 *
 * The banner this hook feeds renders at the BOTTOM of the drawer, under the
 * positions list. With eleven rows above it, a trader who presses EXIT ALL
 * and is refused sees the list not change and nothing else — which is what
 * "why the heck is the exit all btn not working" looked like on 2026-10-26.
 *
 * Getting flat is the one action whose refusal must never need scrolling
 * for. The banner stays, because it persists and the toast does not; this
 * only guarantees the sentence is seen at the moment it is true.
 */
const sayRefused = (label: string, why: string): void =>
  notifyFail(`${label} — not sent`, why)

/**
 * HTTP acceptance is NOT completion. Only the authoritative book removes a
 * position.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * CONCURRENT BY LEG, NOT ONE AT A TIME
 *
 * This held a single pending exit and refused every other one while it was
 * in flight, so pressing EXIT ALL on one basket greyed out EXIT ALL on all
 * of the others until the first settled. Reported on 2026-10-27 with ten
 * positions open: "only the exit btn of the strategy group which was just
 * clicked should be disabled".
 *
 * The rule it was reaching for is real, but it is about LEGS, not about the
 * drawer. The hazard is sizing a second exit against a leg whose first exit
 * has not been accounted for yet — and that is exactly what is refused
 * below, by comparing the legs this request would close against the legs
 * every in-flight request is already closing.
 *
 * Two different baskets share no legs, so there was never anything to
 * refuse. A trader squaring off a book should not have to wait out a round
 * trip per basket to do it.
 *
 * THE SERVER IS STILL THE AUTHORITY, and it guards the same thing more
 * strongly: `exit.service.ts` subtracts each leg's already-committed closing
 * quantity (`alreadySent`, keyed by `tradeLegId`) before sizing anything, so
 * a leg with an exit outstanding is sized to what is LEFT and skipped
 * entirely at zero. This check exists to give the refusal a sentence and to
 * stop the request leaving the browser, not to be the guarantee.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export function useExitOrder(
  book: OpenBookState,
  orders: readonly TrackedIntent[],
  refresh: () => void,
) {
  const [pending, setPending] = useState<ReadonlyMap<string, PendingExit>>(
    new Map(),
  )
  const pendingRef = useRef<Map<string, PendingExit>>(new Map())
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<ExitNotice | null>(null)
  /** Publish the ref. One place, so the two can never disagree. */
  const publish = useCallback(() => setPending(new Map(pendingRef.current)), [])
  const finish = useCallback(
    (which: string) => {
      // Whatever happened — filled, refused, abandoned — the rows this exit
      // claimed belong to the book again. See `markClosing`.
      provisionalStore.clearClosing(pendingRef.current.get(which)?.legIds ?? [])
      pendingRef.current.delete(which)
      publish()
    },
    [publish],
  )
  useEffect(
    () => () => {
      pendingRef.current.clear()
    },
    [],
  )

  /*
   * ── IT RETURNS AN OUTCOME, AND THAT IS NEW ──────────────────────────────
   *
   * Every call site still fires and forgets, because this already sets the
   * banner and toasts the refusal itself. What needs the answer is the one
   * caller that makes SEVERAL of these calls for one gesture — the chain's
   * EXIT ALL, which fans out one request per broker account. Two accounts
   * mean two independent verdicts, and "one succeeded, one refused" is the
   * sentence a trader must get. A single shared `error` string cannot say
   * it: the second writer wins and the first account's fate disappears.
   */
  const exit = useCallback(
    async (
      body: ExitBody,
      which: string,
      broker: OrderBroker,
      label: string,
    ): Promise<ExitOutcome> => {
      // Ref, not just disabled: a double click can arrive before React paints.
      if (pendingRef.current.has(which)) return { kind: 'IGNORED' }
      if (book.kind !== 'READY') {
        setError('Read the current position book before exiting.')
        return {
          kind: 'REFUSED',
          why: 'Read the current position book before exiting.',
        }
      }
      const scoped = book.trades.filter((t) =>
        body.tradeIds.includes(t.tradeId),
      )
      const legIds = scoped.flatMap((t) =>
        t.legs
          .filter(
            (l) =>
              l.netQty !== 0 &&
              // A named leg is the narrowest scope there is, and it wins.
              (body.tradeLegId === undefined ||
                l.tradeLegId === body.tradeLegId) &&
              (!body.tokens ||
                (l.token !== null && body.tokens.includes(l.token))),
          )
          .map((l) => l.tradeLegId),
      )
      if (legIds.length === 0) {
        const why = 'This position is no longer held. Refresh the book.'
        setError(why)
        sayRefused(label, why)
        return { kind: 'REFUSED', why }
      }
      /*
       * THE ONE OVERLAP THAT MATTERS: a leg already being closed.
       *
       * Not "is anything in flight" — that refused two unrelated baskets. A
       * second order sized against a leg whose first exit has not landed yet
       * is the actual hazard, and it is the same leg either way, so the
       * comparison is on leg ids rather than on which button was pressed.
       */
      const closing = new Set(
        [...pendingRef.current.values()].flatMap((p) => p.legIds),
      )
      if (legIds.some((id) => closing.has(id))) {
        const why =
          'An exit is already working for one of these legs. Wait for it to settle.'
        setError(why)
        sayRefused(label, why)
        return { kind: 'REFUSED', why }
      }

      /*
       * THE EXCHANGE IS SHUT — 9.39, the same refusal the chain already made.
       *
       * The server refuses this too, and that is the guarantee; this is the
       * one that spares a round trip and says the right thing. Without it an
       * exit press answered "The price for this contract is too old to trade
       * against" — true, and not the reason, which is that the market closed
       * an hour ago.
       *
       * SIMULATION IS EXEMPT by the same test `placeOrder` uses: the question
       * is not what time it is, it is whether this order is going to a real
       * exchange right now.
       */
      if (
        tickSocket.getMarketContext().source !== 'SIMULATION' &&
        !isOrderWindow()
      ) {
        const why =
          'The market is closed. Orders can be placed between 09:15 and 15:40 IST.'
        setError(why)
        sayRefused(label, why)
        return { kind: 'REFUSED', why }
      }

      const requestId = `exit-${crypto.randomUUID()}`
      const next: PendingExit = {
        which,
        tradeIds: body.tradeIds,
        legIds,
        requestId,
        accepted: false,
        startedAt: Date.now(),
        partial: body.fraction !== undefined && body.fraction < 1,
      }
      pendingRef.current.set(which, next)
      publish()
      /*
       * ── THE LEG READS AS CLOSING FROM THE CLICK — ROADMAP 9.26 ──────────
       *
       * The same reasoning as the provisional entry row: the exit is about
       * two seconds of broker round trips from being visible in the book,
       * and the trader needs to know their click landed now. `legIds` is
       * exactly what this exit will close, computed above from the book
       * itself, so this marks the real rows rather than guessing at them.
       *
       * It changes no quantity and no P&L — the leg still holds what it
       * holds until a fill says otherwise. Cleared in `finish`, whatever the
       * outcome, so a refused exit hands the row straight back.
       */
      provisionalStore.markClosing(legIds)
      setError(null)
      setNotice({ headline: `${label} · submitting…` })
      orderStore.submitting(requestId, label, Date.now(), {
        ...broker,
        mode:
          tickSocket.getMarketContext().source === 'SIMULATION'
            ? 'PAPER'
            : broker.mode,
      })
      try {
        const result = await api.post<ExitResponse>('/market/exit', {
          ...body,
          simulationSessionId: tickSocket.getMarketContext().sessionId,
          clientRequestId: requestId,
        })
        orderStore.acked(
          requestId,
          result.ok,
          result.ok ? result.intentId : null,
          result.ok ? undefined : result.reason,
        )
        // Still OURS? The entry may have been replaced by a later press on the
        // same control, or cleared by unmount.
        if (pendingRef.current.get(which)?.requestId !== requestId)
          return { kind: 'IGNORED' }
        if (!result.ok) {
          setError(result.reason)
          sayRefused(label, result.reason)
          setNotice(null)
          finish(which)
          return { kind: 'REFUSED', why: result.reason }
        }
        {
          const skipped = new Set(result.skipped.map((s) => s.token))
          const legsById = new Map(
            scoped.flatMap((t) =>
              t.legs.map((l) => [l.tradeLegId, l] as const),
            ),
          )
          const accepted = {
            ...next,
            accepted: true,
            legIds: next.legIds.filter((id) => {
              const leg = legsById.get(id)
              return (
                leg?.token === null ||
                leg?.token === undefined ||
                !skipped.has(leg.token)
              )
            }),
          }
          pendingRef.current.set(which, accepted)
          publish()
          setNotice({
            headline: `${label} · accepted — awaiting execution and position confirmation`,
            detail: pacingDetail(result.legCount),
          })
        }
        if (result.skipped.length > 0)
          setError(
            `${result.legCount} leg(s) sent. Could not exit: ${result.skipped.map((s) => `${s.symbol ?? s.token} — ${s.why}`).join('; ')}`,
          )
        refresh()
        return { kind: 'ACCEPTED' }
      } catch (err) {
        if (pendingRef.current.get(which)?.requestId !== requestId)
          return { kind: 'IGNORED' }
        const why =
          err instanceof Error
            ? err.message
            : 'The exit request could not be confirmed.'
        // A lost response may follow a successful write. Never report a network
        // timeout as a rejection, and never automatically resend an exit.
        if (
          err instanceof ApiRequestError &&
          [400, 401, 403, 404, 409, 422, 429].includes(err.status)
        ) {
          orderStore.acked(requestId, false, null, why)
          setError(why)
          sayRefused(label, why)
          setNotice(null)
          finish(which)
          return { kind: 'REFUSED', why }
        }
        orderStore.uncertain(requestId, why)
        setNotice({
          headline: 'Exit confirmation unavailable.',
          detail:
            'Order status is still being checked. Do not submit it again.',
        })
        refresh()
        return { kind: 'UNCERTAIN', why }
      }
    },
    [book, refresh, finish, publish],
  )

  /*
   * Settle each pending exit on its own evidence.
   *
   * Every in-flight exit is walked, not just one: with several baskets
   * closing at once the first to confirm must clear its own card and leave
   * the others alone. The per-exit rules are unchanged — only the loop is
   * new.
   */
  useEffect(() => {
    if (pending.size === 0) return
    let changed = false

    for (const p of pending.values()) {
      const settled = exitSettlement({
        partial: p.partial,
        accepted: p.accepted,
        tradeIds: p.tradeIds,
        legIds: p.legIds,
        order: orders.find((o) => o.clientRequestId === p.requestId),
        trades: book.kind === 'READY' ? book.trades : null,
      })
      if (settled.kind === 'WAITING') continue

      if (settled.kind === 'FAILED') {
        setError(settled.why)
        setNotice(null)
      } else {
        setNotice(settled.notice)
      }
      pendingRef.current.delete(p.which)
      changed = true
    }

    if (changed) {
      publish()
      refresh()
    }
  }, [pending, orders, book, publish, refresh])

  /*
   * One timer for the whole set, armed off the OLDEST exit still in flight.
   *
   * Keyed on the oldest start rather than on a request id: with several in
   * flight, keying on any one of them would re-arm the timer every time
   * another exit was pressed, and the one that is genuinely stuck would
   * never reach fifteen seconds.
   */
  const oldestStart =
    pending.size === 0
      ? null
      : Math.min(...[...pending.values()].map((p) => p.startedAt))
  useEffect(() => {
    if (oldestStart === null) return
    const timer = setTimeout(
      () =>
        setNotice({
          headline: 'An exit is still awaiting confirmation.',
          detail: 'Status checks continue. Do not submit it again.',
        }),
      Math.max(0, oldestStart + 15_000 - Date.now()),
    )
    return () => clearTimeout(timer)
  }, [oldestStart])

  useEffect(() => {
    if (pending.size > 0 || !notice) return
    const timer = setTimeout(() => setNotice(null), 5_000)
    return () => clearTimeout(timer)
  }, [pending, notice])

  /**
   * What is currently being exited, as a SET of the things that were
   * pressed — a tradeId, a tradeLegId, or the manual block's sentinel.
   *
   * A set rather than one id because several can be in flight at once, and
   * the drawer has to disable the card that owns each of them and nothing
   * else.
   */
  const exiting = useMemo<ReadonlySet<string>>(
    () => new Set(pending.keys()),
    [pending],
  )

  return { exit, exiting, error, notice, setError }
}
